/* eslint-disable class-methods-use-this */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Grid } from 'semantic-ui-react';
import ImageBanner from '../../molecules/ImageBanner';
import InternalLink from '../../atoms/InternalLink';
import OutboundLink from '../../atoms/OutboundLink';
import Map from '../../atoms/Map';
import './HeroElement.css';
import Event from '../../darkmatter/Event';
import { GatsbyImagePropType } from '../../services/proptypes';

export default class HeroElement extends React.Component {
  getCTAProps(cta, index) {
    const { color, inverted } = cta;
    const { ctaColor, ctaInverted } = this.props;
    const buttonColor = color === null || color === undefined ? { color: ctaColor } : { color };
    const buttonInverted =
      inverted === null || inverted === undefined ? { inverted: ctaInverted } : {};

    return { className: `cta-${index}`, ...cta, ...buttonColor, ...buttonInverted };
  }

  getOverlayContainerStyle() {
    const { containerAs, containerColor } = this.props;

    if (containerAs === 'solid') return { backgroundColor: containerColor, opacity: 0.9 };
    if (containerAs === 'outline') return { borderStyle: 'solid', borderColor: 'white' };
    return {};
  }

  getColumnStyle(type, value) {
    const { height, textAlign } = this.props;
    const baseStyle = {
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      height,
      display: 'flex',
      alignItems: textAlign,
      justifyContent: 'center',
      padding: '1em',
    };

    return type === 'image'
      ? { ...baseStyle, backgroundImage: `url('${value}')` }
      : { ...baseStyle };
  }

  getBackgroundMetadata() {
    const { images, containerColor, address, showMap } = this.props;
    const displayMap = (address && showMap) || !images || images.length === 0;

    return {
      background: displayMap ? containerColor : images[0],
      content: displayMap ? (
        <div style={{ margin: '1em', padding: '0em' }}>
          <Map address={address} />
        </div>
      ) : null,
      type: 'image',
    };
  }

  requiresOverlay() {
    const { images, overlay, address, showMap } = this.props;

    if (!images || images === []) return false;
    return overlay && !(address && showMap);
  }

  requiresOnlyText() {
    const { images, address } = this.props;

    return (!images || images === []) && !address;
  }

  renderCTA(ctaProps) {
    const { text, link, internal, color, inverted, internalLinkComponent, className } = ctaProps;

    return internal ? (
      <Event event={{ category: 'Hero CTAs', action: 'Click', label: text }} eventType="click">
        <InternalLink
          as={internalLinkComponent || 'a'}
          to={link}
          gatsbyLink={internalLinkComponent !== null && internalLinkComponent !== undefined}
          className={className}
        >
          <Button style={{ marginTop: '1em' }} color={color} inverted={inverted}>
            {text}
          </Button>
        </InternalLink>
      </Event>
    ) : (
      <OutboundLink
        style={{ marginTop: '1em' }}
        color={color}
        inverted={inverted}
        as={Button}
        to={link}
        content={text}
        className={className}
        eventLabel={text}
      />
    );
  }

  renderCTAS() {
    const { ctas } = this.props;

    return <div>{ctas.map((cta, index) => this.renderCTA(this.getCTAProps(cta, index)))}</div>;
  }

  renderHeaderContent() {
    const { header, tagline, ctas, textColor, textAlign } = this.props;
    const forceCenterAlign = this.requiresOverlay() || this.requiresOnlyText();
    const headerStyle = {
      padding: '1em',
      textAlign: forceCenterAlign ? 'center' : textAlign,
    };

    return (
      <div style={headerStyle}>
        {header && <h1 style={{ color: textColor }}>{header}</h1>}
        {tagline && <div style={{ color: textColor }}>{tagline}</div>}
        {ctas && ctas.length > 0 && this.renderCTAS(ctas)}
      </div>
    );
  }

  renderOverlayContainer() {
    const { containerAs, containerColor } = this.props;
    const containerStyle = {
      ...this.getOverlayContainerStyle(),
      padding: '2em',
      justifyContent: 'center',
      display: 'inline-block',
    };

    return (
      <div
        className={`hero-background ${containerAs === 'solid' && containerColor}`}
        style={containerStyle}
      >
        {this.renderHeaderContent()}
      </div>
    );
  }

  renderOverlay() {
    const {
      images,
      showMultiple,
      containerAs,
      overlay,
      parallax,
      width,
      carouselOptions,
    } = this.props;

    let bannerProps = null;

    if (images) {
      bannerProps =
        images.length > 1 && showMultiple
          ? {
              as: 'carousel',
              backgroundImage: images[0],
              galleryImages: images,
              carouselOptions: { ...carouselOptions, autoScroll: true },
            }
          : { as: 'banner', backgroundImage: images[0] };
    }

    return (
      <ImageBanner
        header={this.renderOverlayContainer()}
        parallax={parallax}
        overlay={overlay && containerAs !== 'solid' ? 'dark' : 'none'}
        width={width}
        {...bannerProps}
      />
    );
  }

  renderTiles(items) {
    const { gutter } = this.props;

    return (
      <Grid stackable verticalAlign="middle" columns={2}>
        {items.map(({ background, content, link, type }) => (
          <Grid.Column
            key={link}
            as="a"
            href={link}
            width={type === 'image' ? 11 : 5}
            style={!gutter ? this.getColumnStyle(type, background) : {}}
            className={`hero-background ${background}`}
          >
            <div style={gutter ? this.getColumnStyle(type, background) : {}}>{content}</div>
          </Grid.Column>
        ))}
      </Grid>
    );
  }

  renderTileHero() {
    const { containerColor, textAlign } = this.props;
    const graphic = this.getBackgroundMetadata();
    const block = {
      background: containerColor,
      content: this.renderHeaderContent(),
      type: 'color',
    };

    return (
      <React.Fragment>
        <span className="hero-element-desktop">
          {textAlign === 'right'
            ? this.renderTiles([block, graphic])
            : this.renderTiles([graphic, block])}
        </span>
        <span className="hero-element-mobile">{this.renderOverlay()}</span>
      </React.Fragment>
    );
  }

  renderImagelessHero() {
    const { containerColor } = this.props;

    const imagelessStyle = {
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      height: '15%',
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      justifyContent: 'center',
      padding: '1em',
      backgroundColor: containerColor,
    };

    return (
      <Grid verticalAlign="middle" columns={1}>
        <Grid.Column
          width={16}
          style={imagelessStyle}
          className={`hero-background ${containerColor}`}
        >
          {this.renderHeaderContent()}
        </Grid.Column>
      </Grid>
    );
  }

  render() {
    if (this.requiresOnlyText()) {
      return this.renderImagelessHero();
    }
    return this.requiresOverlay() ? this.renderOverlay() : this.renderTileHero();
  }
}

HeroElement.propTypes = {
  carouselOptions: PropTypes.shape({
    autoScroll: PropTypes.bool,
    autoScrollTime: PropTypes.number,
  }),
  header: PropTypes.string.isRequired,
  tagline: PropTypes.string,
  images: PropTypes.oneOf(
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(GatsbyImagePropType),
  ),
  address: PropTypes.shape({
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    country: PropTypes.string,
    street: PropTypes.string,
  }),
  showMap: PropTypes.bool,
  ctas: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.string,
      internal: PropTypes.bool,
      color: PropTypes.element,
      inverted: PropTypes.string.isRequired,
      className: PropTypes.string,
    }),
  ),
  ctaColor: PropTypes.string,
  ctaInverted: PropTypes.bool,
  containerColor: PropTypes.string,
  textColor: PropTypes.string,
  textAlign: PropTypes.oneOf(['left', 'right', 'center']),
  showMultiple: PropTypes.bool,
  overlay: PropTypes.bool,
  fullWidth: PropTypes.bool,
  parallax: PropTypes.bool,
  containerAs: PropTypes.oneOf(['none', 'outline', 'solid']),
  height: PropTypes.number,
  width: PropTypes.number,
  gutter: PropTypes.bool,
  internalLinkComponent: PropTypes.element,
};

HeroElement.defaultProps = {
  carouselOptions: { autoScroll: true, autoScrollTime: 5000 },
  images: null,
  tagline: null,
  showMap: false,
  address: null,
  ctas: [],
  ctaColor: 'primary',
  ctaInverted: true,
  containerColor: 'primary',
  textColor: 'white',
  textAlign: 'center',
  showMultiple: true,
  overlay: true,
  fullWidth: true,
  parallax: true,
  containerAs: 'solid',
  height: 500,
  gutter: false,
  internalLinkComponent: null,
  width: 2000,
};
